/**
 * Represents a reference from an item to a menu group with pricing context.
 *
 * When an item uses `pricingStrategy: "menu_specific"`, this reference
 * defines the price the item should have when displayed in a specific menu group.
 *
 * This follows the same reference model pattern as:
 * - ItemReference (menu group → item, with priceOverride)
 * - ModifierGroupReference (item → modifier group, with selection rules)
 * - ModifierReference (modifier group → modifier, with priceOverride)
 */
export interface IMenuGroupReference {
  /** The ID of the referenced menu group */
  readonly menuGroupId: number;
  /** The price for this item in this specific menu group */
  readonly price: number;
  /**
   * Full ancestor chain from root → this group, e.g. [100, 200, 300].
   * Used by POS for nearest-ancestor pricing resolution in deeply nested
   * menu group trees. Also enables multi-parent disambiguation when the
   * same group appears under different primary menus with different prices.
   *
   * Optional for backward compat — legacy entries without this field
   * still work via navigation-based groupPath fallback.
   */
  readonly ancestorPath?: number[];
}

export type IMenuGroupReferenceData = Partial<IMenuGroupReference>;

export class MenuGroupReference implements IMenuGroupReference {
  readonly menuGroupId: number;
  readonly price: number;
  readonly ancestorPath?: number[];

  constructor(data: IMenuGroupReferenceData) {
    if (!data.menuGroupId)
      throw new Error("MenuGroupReference: menuGroupId is required");
    if (data.price === undefined || data.price === null)
      throw new Error("MenuGroupReference: price is required");
    this.menuGroupId = data.menuGroupId;
    this.price = data.price;
    this.ancestorPath =
      data.ancestorPath && data.ancestorPath.length > 0
        ? data.ancestorPath
        : undefined;
  }

  toJSON(): IMenuGroupReference {
    const json: IMenuGroupReference = {
      menuGroupId: this.menuGroupId,
      price: this.price,
    };
    if (this.ancestorPath && this.ancestorPath.length > 0) {
      return { ...json, ancestorPath: this.ancestorPath };
    }
    return json;
  }

  static fromJSON(data: Record<string, unknown>): MenuGroupReference {
    if (!data.menuGroupId)
      throw new Error("MenuGroupReference.fromJSON: menuGroupId is missing.");
    if (data.price === undefined || data.price === null)
      throw new Error("MenuGroupReference.fromJSON: price is missing.");

    return new MenuGroupReference({
      menuGroupId: data.menuGroupId as number,
      price: data.price as number,
      ancestorPath: Array.isArray(data.ancestorPath)
        ? (data.ancestorPath as number[])
        : undefined,
    });
  }
}
